import { Injectable } from '@angular/core';

import { Store, createSelector } from '@ngrx/store';
import { Observable, map } from 'rxjs';

import { IBetslipRootState } from '../../../base/store/state';
import { BetslipType } from '../../../core/betslip-type';
import { PickId, V1PickId, V2OptionMarketPickId } from '../../../core/picks/pick-id';
import { ILineSwitcherPickHandlerService } from '../../../integration/services/line-switcher-pick-handler.service';
import { betslipPickIdsSelector } from '../../picks/selectors';
import { betslipCurrentTypeSelector } from '../../types/base/selectors';
import { editBetAddedPicksListSelector, editBetPicksListSelector } from '../../types/selectors';
import { LineSwitcherActions } from '../actions';
import { lineSwitcherSwapPickStateSelector } from '../selector';
import { LineSwitcherUtils } from './line-switcher-utils.service';

export const lineSwitcherPickSelectedSelector = createSelector(
    lineSwitcherSwapPickStateSelector,
    betslipPickIdsSelector,
    betslipCurrentTypeSelector,
    editBetPicksListSelector,
    editBetAddedPicksListSelector,
    (swapPickState, pickState, currentType, editBetPicksList, editBetAddedPicksList) => {
        return { swapPickState, pickState, currentType, editBetPicksList, editBetAddedPicksList };
    },
);

@Injectable()
export class LineSwitcherPickHandlerService implements ILineSwitcherPickHandlerService {
    constructor(private store: Store<IBetslipRootState>) {}

    pickSelected$(id: V1PickId | V2OptionMarketPickId): Observable<boolean> {
        return this.store.select(lineSwitcherPickSelectedSelector).pipe(
            map(({ swapPickState, pickState, currentType, editBetPicksList, editBetAddedPicksList }) => {
                const oldPickId = swapPickState.oldPickId;
                const newPickId = swapPickState.newPickId;

                if (currentType === BetslipType.EditBet) {
                    return (
                        editBetPicksList.some(
                            (pick) =>
                                LineSwitcherUtils.isSportsPick(pick.id) &&
                                pick.id.optionId !== oldPickId.optionId &&
                                pick.id.optionId === id.optionId,
                        ) ||
                        editBetAddedPicksList.some(
                            (pick) =>
                                LineSwitcherUtils.isSportsPick(pick.id) &&
                                pick.id.optionId !== oldPickId.optionId &&
                                pick.id.optionId === id.optionId,
                        ) ||
                        newPickId?.isEqual(id) ||
                        (oldPickId && oldPickId.isEqual(id) && !newPickId)
                    );
                }

                return (
                    pickState.some(
                        (pickId) =>
                            LineSwitcherUtils.isSportsPick(pickId) && pickId.optionId !== oldPickId.optionId && pickId.optionId === id.optionId,
                    ) ||
                    newPickId?.isEqual(id) ||
                    (oldPickId && oldPickId.isEqual(id) && !newPickId)
                );
            }),
        );
    }

    swap(pickId: V1PickId | V2OptionMarketPickId): void {
        this.store.dispatch(LineSwitcherActions.addToSwapSelection({ pickId }));
    }

    readonly currentPick$: Observable<PickId> = this.store.select(lineSwitcherSwapPickStateSelector).pipe(
        map((swapPickState) => {
            const oldPickId = swapPickState.oldPickId;
            const newPickId = swapPickState.newPickId;

            return newPickId ? newPickId : oldPickId;
        }),
    );
}
